import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Avatar,
  Typography,
  Chip,
  List,
  ListItem,
  CircularProgress,
  TextField,
  MenuItem,
  Tooltip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  useTheme,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import ComputerIcon from "@mui/icons-material/Computer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatDate, formatTimestampParts } from "../../utils/dateUtils";
import { CheckStatusChip } from "../CheckStatusChip/CheckStatusChip";
import BlinkingDot from "../BlinkingDot/BlinkingDot";
import {
  CheckStatus,
  AssetInactivityReason,
  inactivityReasonLabel,
  ASSET_OUT_OF_SCOPE_EFFECT,
} from "../../utils/enums";
import {
  GET_ASSET_CHECKS,
  GET_ASSET_CHECK_STATUSES,
  UPDATE_ASSET_DETAILS,
  UPDATE_ASSET_SCOPE,
  DELETE_MANUAL_ASSET,
} from "../../apis/settingsApis/assets-apis/allAssetsApis";
import { GET_USER_DETAILS } from "../../apis/dashboard-apis/dashboardDetailApi";
import ConfirmSnackbar from "../Snackbar/ConfirmSnackbar";
import EditAssetOwnerDialog from "./EditAssetOwnerDialog";
import { isAssetDisposed } from "../../pages/Assets/assetTableConfig";

const normalizeOS = (platform = "", osName = "") => {
  const s = `${platform} ${osName}`.toLowerCase();
  if (s.includes("mac") || s.includes("darwin")) return "mac";
  if (s.includes("win")) return "windows";
  if (s.includes("linux") || s.includes("ubuntu") || s.includes("debian") || s.includes("fedora")) return "linux";
  return null;
};

const SOURCE_STYLE = {
  WATCH_TOWER: { label: "Rego WatchTower", bg: "#e3f2fd", color: "#1976d2" },
  MDM: { label: "MDM", bg: "#ECFEFF", color: "#155E75" },
};

// Fallback title from the check_type when the catalog name is missing.
const titleFromType = (checkType) =>
  String(checkType || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fieldLabelSx = {
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: 0.8,
  fontSize: "0.7rem",
};

// Employees may read every asset field but change none of them, so the inputs
// are disabled rather than hidden. MUI greys disabled text out, which reads as
// "broken control" instead of "value you can't edit" — restore the normal
// colour. The selector covers input, textarea and the select's rendered div.
const readOnlyFieldSx = {
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#1f2937",
    color: "#1f2937",
  },
};

const ADMIN_ONLY_HINT = "Only an admin can change these settings.";

// Explains on hover why a field is read-only. A disabled MUI input receives no
// pointer events, so the tooltip has to hang off a wrapper element or it never
// fires. Renders children untouched when the field is editable.
const AdminOnlyTooltip = ({ show, children }) =>
  show ? (
    <Tooltip title={ADMIN_ONLY_HINT} arrow placement="top">
      <span style={{ display: "block" }}>{children}</span>
    </Tooltip>
  ) : (
    children
  );

const DEVICE_TYPES = ["laptop", "desktop", "router", "phone", "tablet", "server", "other"];

// Deleting a manual asset is wired end to end — the deleteManualAsset mutation,
// the confirmation and the handler below all work — but the button is hidden for
// now. Flip this to true to bring it back; nothing else needs changing.
const SHOW_DELETE_ASSET = false;

// How long a field must be quiet before its edit is written. Long enough that
// typing is one request rather than dozens, short enough that closing the dialog
// straight after a change is unlikely to lose it.
//
// Tuned separately because the two writes cost very different amounts. Asset
// management is a plain column update on assets.assets.
const ASSET_DETAILS_DEBOUNCE_MS = 900;
// The scope reason goes through updateAssetScope, which locks the asset row,
// stamps an audit trail and refetches the list — so it is worth waiting longer
// to be sure the admin has finished typing.
const SCOPE_REASON_DEBOUNCE_MS = 1500;

// Explains why security checks aren't running, shown under the status chip.
const INACTIVITY_HINT = {
  [AssetInactivityReason.LOGGED_OUT]: "Please login to make checks run.",
  [AssetInactivityReason.DISPOSED]: "This asset is disposed — security checks no longer run.",
  [AssetInactivityReason.OUT_OF_SCOPE]: ASSET_OUT_OF_SCOPE_EFFECT,
};

// `onEdit` adds a pencil beside the label, matching how the People page exposes
// its editable fields (Role, Reporting Manager, …). Omit it for read-only facts.
const InfoCell = ({
  label,
  value,
  secondary,
  onEdit,
  editLabel = "Edit",
  editDisabled = false,
}) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="caption" sx={fieldLabelSx}>
        {label}
      </Typography>
      {onEdit && (
        // The span keeps the tooltip working while the button is disabled — a
        // disabled MUI button receives no pointer events, and the disabled case
        // is exactly when the explanation matters most.
        <Tooltip title={editLabel} arrow>
          <span style={{ display: "inline-flex" }}>
            <IconButton
              size="small"
              onClick={onEdit}
              disabled={editDisabled}
              sx={{ p: 0.25, color: "#6b7280", "&:hover": { color: "#1976d2" } }}
            >
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
    <Typography
      variant="body1"
      fontWeight={600}
      sx={{ color: "#1f2937", mt: 0.5, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
    {secondary && (
      <Typography variant="body2" sx={{ color: "#6b7280", wordBreak: "break-word" }}>
        {secondary}
      </Typography>
    )}
  </Box>
);

const CheckRow = ({ index, title, status, detail, checkType, os, isManual }) => {
  // Failing checks always get remediation steps. Manual assets have no agent
  // reporting, so their checks sit pending — surface the steps there too.
  const showRemediation =
    status === CheckStatus.FAIL || (isManual && status === CheckStatus.PENDING);
  const remediationUrl =
    showRemediation && checkType && os
      ? `/agentRemediationSteps?os=${os}&check=${checkType}${isManual ? "&source=manual" : ""}`
      : null;

  return (
    <ListItem
      sx={{
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid #e5e7eb",
        backgroundColor: index % 2 === 0 ? "white" : "#f8fafc",
        px: 2,
        py: 1.5,
        gap: 2,
      }}
    >
      <Chip
        label={`#${index + 1}`}
        size="small"
        sx={{
          height: 26,
          fontSize: "0.75rem",
          fontWeight: 700,
          backgroundColor: "#1976d2",
          color: "white",
          "& .MuiChip-label": { px: 1.25 },
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
          <Typography variant="body1" sx={{ fontWeight: 500, color: "#1e293b", lineHeight: 1.4 }}>
            {title}
          </Typography>
          {remediationUrl && (
            <Box
              component="a"
              href={remediationUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.3,
                color: "#1976d2",
                fontSize: "0.72rem",
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              How to fix
              <OpenInNewIcon sx={{ fontSize: 11 }} />
            </Box>
          )}
        </Box>
        {detail && (
          <Typography variant="caption" sx={{ color: "#6b7280", lineHeight: 1.3, display: "block" }}>
            {detail}
          </Typography>
        )}
      </Box>
      <CheckStatusChip status={status} />
    </ListItem>
  );
};

const isAssetActiveStatus = (asset) => {
  const current = String(asset?.currentStatus ?? "").toLowerCase();
  if (current === "inactive") return false;
  if (current === "active") return true;
  if (asset?.inactivityReason) return false;
  const legacy = String(asset?.status ?? "").toLowerCase();
  if (legacy === "inactive" || legacy === "logged_out" || legacy === "disposed") return false;
  return true;
};

const AssetDetailsDialog = ({ open, onClose, asset, onSaved, isEmployeeView = false }) => {
  const theme = useTheme();

  const isActive = isAssetActiveStatus(asset);
  // Why it's inactive — "Disposed" / "Logged out"; "" while active.
  const inactiveReason = isActive ? "" : inactivityReasonLabel(asset?.inactivityReason);
  // Why checks aren't running (logged out / disposed); "" while active.
  const inactivityHint = isActive
    ? ""
    : INACTIVITY_HINT[String(asset?.inactivityReason || "").toLowerCase()] || "";
  const statusColor = isActive
    ? theme.customColors.status.success.text
    : theme.customColors.status.error.text;

  // Editable, backend-persisted asset fields (device type + disposal date + description).
  const [deviceType, setDeviceType] = useState("");
  const [disposableDate, setDisposableDate] = useState("");
  const [description, setDescription] = useState("");
  const [saveMsg, setSaveMsg] = useState(null);
  // Set by the field onChange handlers only, so seeding the form from the asset
  // never counts as an edit and never triggers a write.
  const [dirty, setDirty] = useState(false);

  // The mutation's own result, held locally. The parents keep `selectedAsset` as
  // a snapshot in state and never re-derive it from the refetched rows, so the
  // `asset` prop stays stale after a scope change — without this the button
  // would not flip to "Restore" until the dialog was reopened.
  const [scopeOverride, setScopeOverride] = useState(null);
  // `inScope` is absent on responses predating the field, so only an explicit
  // false counts as excluded.
  const effectiveInScope = scopeOverride ? scopeOverride.inScope : asset?.inScope;
  const effectiveScopeReason = scopeOverride ? scopeOverride.scopeReason : asset?.scopeReason;
  // A device whose disposal date has passed is out of scope no matter what the
  // admin flag says — that is how the list already groups it, and the toggle has
  // to agree or the dialog contradicts the row the user clicked to open it.
  const isDisposed = isAssetDisposed(asset);
  const isOutOfScope = effectiveInScope === false || isDisposed;
  const [scopeNotes, setScopeNotes] = useState("");
  const [scopeNotesDirty, setScopeNotesDirty] = useState(false);
  const [scopeSnackbar, setScopeSnackbar] = useState(null);

  // Owner is held the same way and for the same reason as scopeOverride: the
  // parent's `asset` snapshot does not refresh, so the field would keep showing
  // the previous owner after a successful reassign.
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [ownerOverride, setOwnerOverride] = useState(null);
  const effectiveOwnerName = ownerOverride ? ownerOverride.ownerName : asset?.ownerName;
  const effectiveOwnerEmail = ownerOverride ? ownerOverride.ownerEmail : asset?.ownerEmail;

  // Collapsed by default: the identity and the security checks are what the
  // dialog is opened for, and these fields are only touched occasionally.
  const [assetMgmtOpen, setAssetMgmtOpen] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Seed the editable fields only when a different asset is opened — not on every
  // object identity change (e.g. a background refetch), so a save's refetch keeps
  // the entered values while the status chip still reflects fresh data.
  useEffect(() => {
    setDeviceType(asset?.deviceType || "");
    setDisposableDate(asset?.disposableDate ? String(asset.disposableDate).slice(0, 10) : "");
    setDescription(asset?.description || "");
    setSaveMsg(null);
    setDirty(false);
    setScopeNotes(asset?.inScope === false ? asset?.scopeReason || "" : "");
    setScopeNotesDirty(false);
    setScopeSnackbar(null);
    setScopeOverride(null);
    setOwnerOverride(null);
    setDeleteConfirmOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.assetId]);

  const [updateAssetDetails] = useMutation(UPDATE_ASSET_DETAILS);
  const [updateAssetScope] = useMutation(UPDATE_ASSET_SCOPE);
  const [deleteManualAsset] = useMutation(DELETE_MANUAL_ASSET);
  // Names the acting admin in the default out-of-scope reason, as People does.
  const { data: currentUserData } = useQuery(GET_USER_DETAILS, { skip: isEmployeeView });
  const currentUserName =
    currentUserData?.getAccountDetails?.userDetails?.name || "";
  // Covers the whole save → refetch cycle, so the loader spans the gap until the
  // status/scope actually reflect the change (not just the mutation).
  const [saving, setSaving] = useState(false);

  // Device type is only editable for manually added assets. For WatchTower/MDM
  // assets it's detected by the agent/MDM and a sync would overwrite any edit.
  const isManual = asset?.source === "MANUAL";

  // Owner is locked for the same reason, and it is not a cosmetic lock: for a
  // WatchTower device persistSync writes owner_employee_id from whoever is signed
  // into the agent, and an MDM sync writes whoever the MDM says. Reassigning
  // either would silently revert on the next sync, so say so instead.
  const syncSourceLabel =
    asset?.source === "WATCH_TOWER" ? "Rego WatchTower" : asset?.integrationName || "your MDM";
  const ownerEditHint = isManual
    ? "Change owner"
    : `Owner is set by ${syncSourceLabel} and cannot be reassigned manually. In order to reassign please contact our team.`;

  // Debounced auto-save. Runs only once `dirty` is set, which the field onChange
  // handlers do — the effect above seeds the same state from the asset, and
  // without that guard opening a dialog would immediately write the values it
  // had just read back to the server.
  useEffect(() => {
    if (!dirty || isEmployeeView || !asset?.assetId) return;
    const timer = setTimeout(() => {
      persistDetails();
    }, ASSET_DETAILS_DEBOUNCE_MS);
    // Every keystroke cancels the pending write and starts the wait again, so a
    // burst of typing costs one request rather than one per character.
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, deviceType, description, disposableDate, isEmployeeView, asset?.assetId]);

  const persistDetails = async () => {
    // updateAssetDetails is open to Roles.EMPLOYEE server-side, so refuse here
    // too rather than rely on the fields being disabled.
    if (isEmployeeView) return;
    setSaveMsg(null);
    setSaving(true);
    try {
      const { data } = await updateAssetDetails({
        variables: {
          assetId: asset.assetId,
          // Partial update: only send deviceType when it's ours to change.
          // (It can't be emptied, so an empty value is skipped too.)
          deviceType: isManual && deviceType ? deviceType : undefined,
          description: description || null,
          // Employees can't change disposal date — only admins can.
          disposableDate: isEmployeeView
            ? undefined
            : disposableDate
              ? `${disposableDate}T00:00:00Z`
              : null,
        },
      });
      // Cleared before the refetch: a keystroke landing while it is in flight
      // must leave the form dirty so the next debounce picks it up.
      setDirty(false);
      // Wait for the refetch so the table + recomputed status reflect the change
      // before we drop the loader — no gap where "Saved" shows but status is stale.
      await onSaved?.();
      setSaveMsg({
        type: "success",
        text: data?.updateAssetDetails?.message || "Saved",
      });
    } catch (e) {
      // Left dirty on failure so the next edit retries rather than silently
      // dropping the change.
      setSaveMsg({ type: "error", text: e.message || "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  // Notes are optional in the UI, matching the People page, but the API rejects a
  // blank reason — so fall back to naming the admin who did it.
  const defaultOutOfScopeReason = () =>
    `Marked out of scope by ${currentUserName?.trim() || "admin"}`;

  const notify = (message) =>
    setScopeSnackbar({ message, vertical: "top", horizontal: "right" });

  const applyScope = async (nextInScope, reason) => {
    const { data } = await updateAssetScope({
      variables: {
        assetId: asset.assetId,
        inScope: nextInScope,
        scopeReason: nextInScope ? null : reason,
      },
    });
    const res = data?.updateAssetScope;
    if (res?.success === false) throw new Error(res.message || "Failed to update scope");
    // Trust the mutation's own result for this dialog. The parents hold
    // `selectedAsset` as a snapshot and never re-derive it from the refetched
    // rows, so the `asset` prop stays stale and the toggle would snap back.
    setScopeOverride({
      inScope: res?.data?.inScope ?? nextInScope,
      scopeReason: res?.data?.scopeReason ?? null,
    });
    await onSaved?.();
    return res;
  };

  // Same debounce as the detail fields. `scopeNotesDirty` keeps the seeded value
  // from being written straight back when the dialog opens on an excluded asset.
  useEffect(() => {
    if (!scopeNotesDirty || !isOutOfScope || isEmployeeView) return;
    const timer = setTimeout(() => {
      persistScopeNotes();
    }, SCOPE_REASON_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeNotesDirty, scopeNotes, isOutOfScope, isEmployeeView]);

  // Closing the dialog unmounts the debounce effects, and their cleanup cancels
  // whatever write was still pending — so an edit made in the last ~900ms would
  // vanish without trace. Flush instead of cancelling.
  const handleClose = () => {
    if (!isEmployeeView) {
      if (dirty) persistDetails();
      if (scopeNotesDirty) persistScopeNotes();
    }
    onClose?.();
  };

  const handleScopeToggle = async (targetInScope) => {
    if (targetInScope === !isOutOfScope) return;
    const reason = targetInScope ? null : scopeNotes.trim() || defaultOutOfScopeReason();
    if (!targetInScope) setScopeNotes(reason);
    // Optimistic, reverted on failure — the toggle should move under the cursor.
    setScopeOverride({ inScope: targetInScope, scopeReason: reason });
    try {
      await applyScope(targetInScope, reason);
      notify(`Asset marked as ${targetInScope ? "In Scope" : "Out of Scope"}`);
    } catch (e) {
      setScopeOverride(null);
      notify(e.message || "Failed to update scope");
    }
  };

  // Debounced like the detail fields, rather than saved on blur — the reason box
  // sits in the header where there is nothing obvious to click away to, so a
  // blur-only save was easy to miss entirely.
  const persistScopeNotes = async () => {
    if (!isOutOfScope || isEmployeeView) return;
    const trimmed = scopeNotes.trim();
    // Nothing changed (including the seeded value on open) — no write.
    if (trimmed === String(effectiveScopeReason || "").trim()) return;
    try {
      await applyScope(false, trimmed || defaultOutOfScopeReason());
    } catch (e) {
      notify(e.message || "Failed to save notes");
    }
  };

  const handleDelete = async () => {
    if (!asset?.assetId || isEmployeeView) return;
    setDeleting(true);
    try {
      const { data } = await deleteManualAsset({ variables: { assetId: asset.assetId } });
      const res = data?.deleteManualAsset;
      if (!res?.success) throw new Error(res?.message || "Failed to delete asset");
      setDeleteConfirmOpen(false);
      // Refresh the list before closing, so the row is already gone behind us.
      await onSaved?.();
      onClose();
    } catch (e) {
      notify(e.message || "Failed to delete asset");
    } finally {
      setDeleting(false);
    }
  };

  const src = SOURCE_STYLE[asset?.source] || SOURCE_STYLE.MDM;
  // Email, not the display name: it is the unambiguous identifier, and it is what
  // an admin needs when deciding who to contact. Name is only a fallback for the
  // rare row whose owner has no work email resolved.
  const owner = effectiveOwnerEmail || effectiveOwnerName || null;

  const { data: catalogData, loading: catalogLoading } = useQuery(
    GET_ASSET_CHECKS,
    { skip: !open || !isActive, fetchPolicy: "cache-and-network" }
  );

  // Use embedded checkStatus if available (from getMyAssets), otherwise fetch separately.
  const hasEmbeddedChecks = Boolean(asset?.checkStatus);
  const { data: statusData, loading: statusLoading } = useQuery(
    GET_ASSET_CHECK_STATUSES,
    {
      variables: { assetId: asset?.assetId },
      skip: !open || !isActive || !asset?.assetId || hasEmbeddedChecks,
      fetchPolicy: "cache-and-network",
    }
  );

  const checksLoading = catalogLoading || (!hasEmbeddedChecks && statusLoading);
  const catalog = catalogData?.getAssetChecks || [];
  const assetStatuses = hasEmbeddedChecks
    ? asset.checkStatus.checks || []
    : statusData?.getAssetCheckStatuses?.[0]?.checks || [];

  const os = normalizeOS(asset?.platform, asset?.osName);

  const buildDetail = (status, updatedAt, statusReason) => {
    let lastChecked = "";
    if (status !== CheckStatus.DISABLED && updatedAt) {
      try {
        lastChecked = formatDate(updatedAt);
      } catch {
        lastChecked = "";
      }
    }
    return statusReason || (lastChecked ? `Last checked ${lastChecked}` : "");
  };

  // Preferred: drive the list from the full catalog (so every check shows,
  // pending ones included). Match the asset's status by id, falling back to
  // check_type when the two id spaces don't line up.
  const catalogChecks = catalog.map((cat) => {
    const latest =
      assetStatuses.find((s) => String(s.activeCheckId) === String(cat.activeCheckId)) ||
      assetStatuses.find((s) => s.checkType && s.checkType === cat.activeCheckType);
    const status = latest?.status || CheckStatus.PENDING;
    return {
      key: cat.activeCheckId,
      title: cat.activeCheckName || titleFromType(cat.activeCheckType),
      checkType: cat.activeCheckType,
      status,
      detail: buildDetail(status, latest?.updatedAt, latest?.statusReason),
    };
  });

  // Fallback: catalog is empty (not configured / not yet loaded) → render the
  // asset's own checks directly, so the dialog always matches the status chips
  // shown in the table row.
  const embeddedChecks = assetStatuses.map((s) => {
    const status = s.status || CheckStatus.PENDING;
    return {
      key: s.activeCheckId || s.checkType,
      title: s.activeCheckName || titleFromType(s.checkType),
      checkType: s.checkType,
      status,
      detail: buildDetail(status, s.updatedAt, s.statusReason),
    };
  });

  const checks = catalog.length ? catalogChecks : embeddedChecks;

  if (!asset) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      // Pinned to the top rather than vertically centred: centring makes MUI
      // re-centre the dialog whenever its height changes, so it drifted up the
      // screen as content appeared.
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          mt: "2.5vh",
          // A fixed height, not maxHeight. Toggling the scope reveals the notes
          // box and toggling the tabs adds rows, and with an elastic height the
          // whole dialog resized under the cursor. Now the frame is constant and
          // only the scroll area inside it changes.
          height: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.2rem",
          color: theme.customColors.text.strong,
          background: "#F7F7FC",
          py: 2,
          px: 3,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Device Details
        <IconButton onClick={handleClose} sx={{ color: theme.customColors.icons.dark }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: "#f8fafc" }}>
        {/* Header */}
        <Box sx={{ p: 3, backgroundColor: "white", borderBottom: "1px solid #e5e7eb" }}>
          {/* Top-aligned: the notes box below the scope toggle changes this row's
              height, and centering would drag the avatar and name down with it. */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, minWidth: 0 }}>
              <Avatar sx={{ width: 64, height: 64, backgroundColor: "#e3f2fd", color: "#1976d2" }}>
                <ComputerIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <BlinkingDot
                    color={
                      isActive
                        ? theme.customColors.status.success.text
                        : theme.customColors.status.error.text
                    }
                    size={18}
                    dotSize={9}
                    sx={{ ml: 0 }}
                  />
                  <Typography variant="h5" fontWeight={600} sx={{ color: "#1f2937" }} noWrap>
                    {asset.name || asset.hostname || "Unknown device"}
                  </Typography>
                </Box>
                {/* The only place the owner is shown or changed — it used to also
                    appear as a cell in the facts grid below, which meant two
                    readings of the same field on one screen.
                    The pencil is admin-only: on the employee page the owner is
                    always the person reading it, since getMyAssets filters on
                    their own employee_data.id. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: "#6b7280" }} noWrap>
                    {owner || "Unassigned"}
                  </Typography>
                  {!isEmployeeView && (
                    <Tooltip title={ownerEditHint} arrow>
                      <span style={{ display: "inline-flex" }}>
                        <IconButton
                          size="small"
                          onClick={() => setOwnerDialogOpen(true)}
                          disabled={!isManual}
                          sx={{ p: 0.25, color: "#6b7280", "&:hover": { color: "#1976d2" } }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                ml: "auto",
                // Pinned to the top so the avatar/name stay put when the
                // out-of-scope notes box expands this column.
                alignSelf: "flex-start",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <Chip
                label={asset.source === "WATCH_TOWER" ? src.label : asset.integrationName || src.label}
                sx={{ backgroundColor: src.bg, color: src.color, fontWeight: 600, fontSize: "0.78rem" }}
              />

              {/* Compliance scope — admin only. Employees must not be able to pull
                  their own device out of scope, which is also why the API puts this
                  behind requireAdmin on a separate mutation from updateAssetDetails. */}
              {!isEmployeeView && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  {/* Disposal already decides the scope, and it is undone by
                      clearing the disposal date, not from here. So there is no
                      control to offer — state it and move on, rather than render
                      a toggle that cannot be used. */}
                  {isDisposed ? (
                    <Chip
                      label="Out of Scope · Disposed"
                      sx={{
                        backgroundColor: theme.customColors.status.error.bg,
                        color: theme.customColors.status.error.text,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    />
                  ) : (
                  <ToggleButtonGroup
                    value={isOutOfScope ? "out_of_scope" : "in_scope"}
                    exclusive
                    size="small"
                    onChange={(_, newValue) => {
                      if (!newValue) return;
                      handleScopeToggle(newValue === "in_scope");
                    }}
                    sx={{
                      borderRadius: "26px",
                      border: "1px solid #E8EAED",
                      gap: 0.2,
                      overflow: "hidden",
                      boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
                      "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: "36px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        px: 2,
                        py: 0.75,
                        minWidth: 88,
                        color: "#6B7280",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s ease-in-out",
                        "&.Mui-selected": {
                          backgroundColor: "#3B82F6",
                          color: "#fff",
                          boxShadow: "inset 0 1px 2px 0 rgba(0,0,0,0.05)",
                          "&:hover": { backgroundColor: "#2563EB" },
                        },
                        "&:hover:not(.Mui-selected)": {
                          backgroundColor: "#F9FAFB",
                          color: "#374151",
                        },
                      },
                      "& .MuiToggleButtonGroup-grouped": { border: "none" },
                    }}
                  >
                    <Tooltip title={isOutOfScope ? "Mark as In Scope" : "Currently In Scope"} arrow>
                      <span>
                        <ToggleButton value="in_scope">In Scope</ToggleButton>
                      </span>
                    </Tooltip>
                    {/* What excluding actually does is explained once, behind the
                        info icon in the page's intro card — not repeated here. */}
                    <Tooltip
                      title={isOutOfScope ? ASSET_OUT_OF_SCOPE_EFFECT : "Mark as Out of Scope"}
                      arrow
                    >
                      <span>
                        <ToggleButton value="out_of_scope">Out of Scope</ToggleButton>
                      </span>
                    </Tooltip>
                  </ToggleButtonGroup>
                  )}

                  {/* The notes explain an admin's decision to exclude a device.
                      Disposal is not a decision that needs one — the disposal date
                      is the reason — so the box stays hidden there. */}
                  {isOutOfScope && !isDisposed && (
                    <TextField
                      size="small"
                      multiline
                      rows={2}
                      placeholder="Additional notes (optional)"
                      value={scopeNotes}
                      onChange={(e) => {
                        setScopeNotes(e.target.value);
                        setScopeNotesDirty(true);
                      }}
                      sx={{
                        mt: 1,
                        width: 260,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          fontSize: "0.8125rem",
                          lineHeight: 1.5,
                          color: "#374151",
                          backgroundColor: "#FAFBFC",
                          "& fieldset": { borderColor: "#E5E7EB", borderWidth: "1px" },
                          "&:hover fieldset": { borderColor: "#D1D5DB" },
                          "&.Mui-focused": {
                            backgroundColor: "#fff",
                            boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.12)",
                            "& fieldset": { borderColor: "#93C5FD", borderWidth: "1px" },
                          },
                          "& textarea": {
                            color: "#374151",
                            "&::placeholder": { color: "#9CA3AF", opacity: 1 },
                          },
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderTop: "1px solid #e5e7eb" }}>
          {/* Facts grid */}
          <Box sx={{ p: 3, backgroundColor: "white", mx: 3, mt: 3, borderRadius: 2, border: "1px solid #e5e7eb" }}>
            {/* Hostname is deliberately not shown: it reads as another name for
                the device and rarely matches the one in the header, which left
                people unsure which was the real one. Serial number is the
                identifier that matters here. */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 3 }}>
              <InfoCell label="Serial Number" value={asset.serialNumber} />
              <InfoCell label="Last Sync" value={asset.lastActivityAt ? formatTimestampParts(asset.lastActivityAt, "Local").full : "Never"} />
            </Box>
          </Box>

          {/* Asset management — current status (read-only) + editable disposal date
              & description. Admin-only: every field in here is either theirs to
              change or theirs to act on, and an employee could only ever read it,
              so the whole block is hidden rather than shown greyed out. */}
          {!isEmployeeView && (
          <Box sx={{ p: 3, backgroundColor: "white", mx: 3, mt: 3, borderRadius: 2, border: "1px solid #e5e7eb" }}>
            <Box
              onClick={() => setAssetMgmtOpen((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ color: "#1f2937" }}>
                Asset Management
              </Typography>
              <ExpandMoreIcon
                sx={{
                  color: "#6b7280",
                  transition: "transform 0.2s ease",
                  transform: assetMgmtOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Box>

            <Collapse in={assetMgmtOpen} timeout="auto" unmountOnExit>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 3, mt: 2 }}>
              <Box>
                <Typography variant="caption" sx={fieldLabelSx}>Current Status</Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Chip
                    label={isActive ? "Active" : inactiveReason ? `Inactive · ${inactiveReason}` : "Inactive"}
                    sx={{
                      backgroundColor: isActive
                        ? theme.customColors.status.success.bg
                        : theme.customColors.status.error.bg,
                      color: statusColor,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderRadius: 0,
                      border: "none",
                    }}
                  />
                  {inactivityHint && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.75, color: theme.customColors.text.secondary, lineHeight: 1.4 }}
                    >
                      {inactivityHint}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" sx={fieldLabelSx}>Device Type</Typography>
                <Tooltip
                  title={
                    isEmployeeView
                      ? ADMIN_ONLY_HINT
                      : isManual
                        ? ""
                        : `Detected by ${asset.source === "WATCH_TOWER" ? "Rego WatchTower" : asset.integrationName || "your MDM"} — can't be edited.`
                  }
                  arrow
                  placement="top"
                >
                  <span style={{ display: "block" }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      disabled={!isManual || isEmployeeView}
                      value={deviceType}
                      onChange={(e) => {
                        setDeviceType(e.target.value);
                        setDirty(true);
                      }}
                      sx={{
                        mt: 0.5,
                        textTransform: "capitalize",
                        ...(isEmployeeView && readOnlyFieldSx),
                      }}
                    >
                      {DEVICE_TYPES.map((t) => (
                        <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                  </span>
                </Tooltip>
              </Box>

              <Box>
                <Typography variant="caption" sx={fieldLabelSx}>Disposal Date</Typography>
                <AdminOnlyTooltip show={isEmployeeView}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={disposableDate}
                    onChange={
                      isEmployeeView
                        ? undefined
                        : (e) => {
                            setDisposableDate(e.target.value);
                            setDirty(true);
                          }
                    }
                    disabled={isEmployeeView}
                    InputLabelProps={{ shrink: true }}
                    inputProps={isEmployeeView ? { readOnly: true } : undefined}
                    sx={{ mt: 0.5, ...(isEmployeeView && readOnlyFieldSx) }}
                  />
                </AdminOnlyTooltip>
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" sx={fieldLabelSx}>Description</Typography>
              <AdminOnlyTooltip show={isEmployeeView}>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  placeholder={isEmployeeView ? "No description" : "Add a note about this device…"}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDirty(true);
                  }}
                  disabled={isEmployeeView}
                  sx={{ mt: 0.5, ...(isEmployeeView && readOnlyFieldSx) }}
                />
              </AdminOnlyTooltip>
            </Box>

            {/* Nothing here is editable for an employee, so there is nothing to
                report — hide the whole row. */}
            {!isEmployeeView && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, minHeight: 24 }}>
              {/* Edits save themselves, so this replaces the Save button. The row
                  keeps a minHeight so the panel does not jump as the status
                  appears and clears. */}
              {saving ? (
                <>
                  <CircularProgress size={14} sx={{ color: "#6b7280" }} />
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    Saving…
                  </Typography>
                </>
              ) : saveMsg ? (
                <Typography
                  variant="body2"
                  sx={{ color: saveMsg.type === "success" ? "#15803d" : "#b91c1c" }}
                >
                  {saveMsg.text}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                  Changes save automatically
                </Typography>
              )}

              {/* Manual assets only. Anything Rego WatchTower or an MDM reports
                  would be recreated by the next sync, so the API refuses it and
                  offering the button here would just be a trap. */}
              {isManual && SHOW_DELETE_ASSET && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={saving || deleting}
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    ml: "auto",
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                >
                  Delete Asset
                </Button>
              )}
            </Box>
            )}
            </Collapse>

          </Box>
          )}

          {/* Security checks only for active assets (hidden for inactive in both admin & employee). */}
          {isActive ? (
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <Box sx={{ px: 2, py: 1.5, backgroundColor: "#f8fafc", borderBottom: "1px solid #ddd" }}>
                <Typography variant="body1" fontWeight="bold">Security Checks</Typography>
              </Box>
              {checksLoading && checks.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : checks.length === 0 ? (
                <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    No compliance checks have run for this device yet.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {checks.map((c, i) => (
                    <CheckRow key={c.key} index={i} title={c.title} status={c.status} detail={c.detail} checkType={c.checkType} os={os} isManual={isManual} />
                  ))}
                </List>
              )}
            </Box>
          </Box>
          ) : null}
        </Box>
      </DialogContent>

      <EditAssetOwnerDialog
        open={ownerDialogOpen}
        onClose={() => setOwnerDialogOpen(false)}
        asset={asset}
        onSaved={async (updated) => {
          // Hold the mutation's own result: the parent's `asset` snapshot does
          // not refresh, so without this the field snaps back to the old owner.
          if (updated) {
            setOwnerOverride({
              ownerName: updated.ownerName,
              ownerEmail: updated.ownerEmail,
            });
          }
          await onSaved?.();
        }}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => (deleting ? null : setDeleteConfirmOpen(false))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete this asset?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#374151" }}>
            <strong>{asset?.name || asset?.hostname || asset?.serialNumber}</strong> will be
            removed from your asset list. Its compliance history is retained, and the serial
            number can be added again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmSnackbar snackbarInfo={scopeSnackbar} />
    </Dialog>
  );
};

export default AssetDetailsDialog;
